/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  JobContractDaysPeriodType,
  ScheduleDetailsDto,
  ScheduleDetailsTaskDto,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskType,
  ScheduleType,
} from '@gh/builder-shared-data';
import { isDirtyTypeNew } from '@gh/common-shared';
import { DirtyType, EMPTY_ID, ID, WithVersion } from '@gh/common-shared-data';
import { cast, isNotNil, setHours, shiftHours } from '@gh/core-util';
import {
  calculateHierarchicalRelations,
  GANTT_SCHEDULE_UNIQUE_ID,
  GANTT_WORKING_HOURS,
  GanttTask,
  ganttTaskRelationFromDto,
  ganttTaskRelationToDto,
  genGanttTaskUniqueId,
  getGanttTaskNumberFromId,
  HOURS_END_OF_DAY,
} from '@gh/builder-shared-gantt-chart';
import { map, omit, trim } from 'lodash';

function scheduleGanttTaskNotesToDto(task: ScheduleGanttTask): NonNullable<ScheduleDetailsTaskDto['notes']> {
  const list = task.notes;
  if (list?.length) {
    return list
      .filter((n: { memo?: string }) => trim(n?.memo || '').length > 0)
      .map((n: Record<string, unknown>) => omit(n, ['uniqueId']) as { memo: string; scheduleTaskNoteId?: ID });
  }
  const memo = trim(task.recentNote?.memo ?? task.noteMemo ?? '');
  if (memo.length > 0) {
    const id = task.recentNote?.scheduleTaskNoteId;
    return isNotNil(id) ? [{ memo, scheduleTaskNoteId: id }] : [{ memo }];
  }
  return [];
}

export type ScheduleGanttTask = GanttTask & {
  isSlab: boolean;
  baseEndDate: Date;
  description?: string;
  costCenterId?: ID;
  costCenterCode?: string;
  orderNumber?: string;
  baseStartDate: Date;
  completionDate?: Date;
  criticalPathId?: ID;
  isContractStart: boolean;
  supplierContactId?: ID;
  isFromCriticalPath: boolean;
  isInSupervisorSchedule: boolean;
  changeDateAfterComplete?: ScheduleTaskChangeDateAfterCompleteType;
  isExcludeFromGanttChart: boolean;
  mayHaveResource: boolean;
  status?: number;
};
export type ScheduleGanttDetails = WithVersion<{
  scheduleId: ID;
  authToCommenceDate: Date;
  jobNumber: string;
  name: string;
  jobId?: ID;
  salesFolderId?: ID;
  salesFolderName: string;
  isFinalized: boolean;
  isNotProceeding: boolean;
  isSalesFolderActive: boolean;
  startDate: Date;
  endDate: Date;
  duration: number;
  type: ScheduleType;
  tasks: ScheduleGanttTask[];
  jobContractConstructionPeriodIncludes: JobContractDaysPeriodType;
  isBaseDatesSavedBefore: boolean;
  gDriveUrl?: string;
}>;

export const scheduleGanttDetailsFromDto = (dto: ScheduleDetailsDto): ScheduleGanttDetails => ({
  ...cast<ScheduleGanttDetails>(omit(dto, 'tasks')),
  tasks: calculateHierarchicalRelations(dto.tasks.map((task) => ({
    ...cast<ScheduleGanttTask>(omit(task, 'scheduleTaskId', 'parentTaskNumber', 'duration')),
    duration      : task.duration / GANTT_WORKING_HOURS,
    completionPct : task.completionPct || 0,
    physicalTaskId: task.scheduleTaskId!,
    scheduleId    : GANTT_SCHEDULE_UNIQUE_ID,
    taskId        : genGanttTaskUniqueId(task.taskNumber),
    parentTaskId  : isNotNil(task.parentTaskNumber) ? genGanttTaskUniqueId(task.parentTaskNumber) : void 0,
    opened        : false,
    notes         : task.notes ?? [],
    noteMemo      : task.recentNote ? task.recentNote.memo : '',
    resource      : {
      name: task.resource,
      id  : task.supplierContactId || task.employeeContactId,
      isCcSupplier: !!task.supplierContactId,
    },
    predecessors  : map(
      task.predecessors,
      (predecessor) => ganttTaskRelationFromDto(GANTT_SCHEDULE_UNIQUE_ID, predecessor),
    ),
  }))),
});

export const scheduleGanttDetailsToDto = (model: ScheduleGanttDetails): ScheduleDetailsDto => ({
  ...cast<ScheduleDetailsDto>(omit(model, 'tasks')),
  tasks: model.tasks.map((task) => ({
    ...cast<ScheduleDetailsTaskDto>(omit(
      task,
      [
        'scheduleId', 'physicalTaskId', 'taskId', 'parentTaskId',
        'opened', 'hasChildren', 'canBeDeleted', 'dirtyType', 'entityState', 'hasChildrenExcludedFromGanttChart',
        'duration', 'selected', 'notes', 'noteMemo', 'mayHaveResource', 'recentNote',
      ],
    )),
    type             : task.hasChildrenExcludedFromGanttChart && task.type === ScheduleTaskType.Empty
      ? ScheduleTaskType.Summary
      : task.type,
    notes            : scheduleGanttTaskNotesToDto(task),
    resource         : task.resource && task.resource.name ? task.resource.name : undefined,
    supplierContactId: task.resource && task.resource.id && !task.resource.isEmployee ? task.resource.id : undefined,
    employeeContactId: task.resource && task.resource.id && task.resource.isEmployee ? task.resource.id : undefined,
    scheduleTaskId   : isDirtyTypeNew(task.dirtyType) ? void 0 : task.physicalTaskId,
    parentTaskNumber : task.parentTaskId ? getGanttTaskNumberFromId(task.parentTaskId, model.tasks) : void 0,
    predecessors     : map(task.predecessors, ganttTaskRelationToDto),
    duration         : task.duration * GANTT_WORKING_HOURS,
  })),
});

export const newScheduleEmptyTask = (
  taskNumber: number,
  name: string,
  startDate: Date,
  parentTaskId?: ID,
): ScheduleGanttTask =>
  ({
    physicalTaskId                   : EMPTY_ID,
    scheduleId                       : GANTT_SCHEDULE_UNIQUE_ID,
    taskId                           : genGanttTaskUniqueId(taskNumber),
    type                             : ScheduleTaskType.Empty,
    hasChildrenExcludedFromGanttChart: false,
    taskNumber,
    parentTaskId,
    name,
    isSlab                           : false,
    isInSupervisorSchedule           : false,
    isExcludeFromGanttChart          : false,
    isFromCriticalPath               : false,
    isResourceInUse                  : false,
    startDate,
    mayHaveResource                  : true,
    baseStartDate                    : startDate,
    endDate             : setHours(startDate, HOURS_END_OF_DAY),
    baseEndDate         : setHours(startDate, HOURS_END_OF_DAY),
    duration            : 1,
    completionPct       : 0,
    canBeDeleted        : true,
    opened              : false,
    hasChildren         : false,
    dirtyType           : DirtyType.New,
    isContractStart     : false,
    predecessors        : [],
    todo                : [],
    notes               : [],
    canBeMarkedAsDeleted: false,
    markedAsDeleted     : false,
  });

export const newScheduleCloneTask = (
  taskNumber: number,
  name: string,
  clonedTask: GanttTask,
): ScheduleGanttTask =>
  ({
    physicalTaskId                   : EMPTY_ID,
    scheduleId                       : GANTT_SCHEDULE_UNIQUE_ID,
    taskId                           : genGanttTaskUniqueId(taskNumber),
    type                             : clonedTask.type,
    hasChildrenExcludedFromGanttChart: false,
    taskNumber,
    parentTaskId                     : clonedTask.parentTaskId,
    name,
    isSlab                           : false,
    isInSupervisorSchedule           : false,
    isExcludeFromGanttChart          : false,
    isFromCriticalPath               : false,
    isResourceInUse                  : false,
    startDate                        : clonedTask.startDate,
    mayHaveResource                  : true,
    baseStartDate                    : clonedTask.startDate,
    endDate                          : shiftHours(clonedTask.startDate, GANTT_WORKING_HOURS),
    baseEndDate                      : shiftHours(clonedTask.startDate, GANTT_WORKING_HOURS),
    duration                         : 1,
    completionPct                    : 0,
    canBeDeleted                     : true,
    opened                           : false,
    hasChildren                      : false,
    dirtyType                        : DirtyType.New,
    isContractStart                  : false,
    predecessors                     : [],
    todo                             : [],
    notes                            : clonedTask.notes,
    description                      : clonedTask.description,
    resource                         : clonedTask.resource,
    canBeMarkedAsDeleted             : false,
    markedAsDeleted                  : false,
  });
